import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { sa } from '../../superAdminUi';
import { hintSx, noteSx, tone } from './panelStyles';

const STATUS_ICON = {
  pending: <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: sa.color.stroke }} />,
  running: <CircularProgress size={18} sx={{ color: sa.color.teal, m: '1px' }} />,
  done: <CheckCircleOutlineIcon sx={{ fontSize: 20, color: tone.added.fg }} />,
  failed: <ErrorOutlineIcon sx={{ fontSize: 20, color: tone.removed.fg }} />,
};

// Each save step with its own status, so a failure names exactly what didn't happen — and what
// already did — instead of a generic "some changes were saved".
function SaveProgress({ steps }) {
  const failed = steps.find((step) => step.status === 'failed');
  const allDone = steps.every((step) => step.status === 'done');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box component='ol' sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {steps.map((step) => (
          <Box component='li' key={step.key} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }} data-step={step.key} data-status={step.status}>
            {STATUS_ICON[step.status]}
            <Box>
              <Typography
                sx={{
                  fontFamily: sa.font.family,
                  fontSize: 14,
                  color: step.status === 'failed' ? tone.removed.fg : step.status === 'pending' ? sa.color.textGray : sa.color.text,
                }}
              >
                {step.label}
              </Typography>
              {step.error && <Typography sx={{ ...hintSx, color: tone.removed.fg }}>{step.error}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>

      {allDone && <Box sx={noteSx(tone.added)} role='status'>All changes saved.</Box>}
      {failed && (
        <Box sx={noteSx(tone.removed)} role='alert'>
          {steps.some((step) => step.status === 'done')
            ? 'The steps marked done are saved. Retry to finish the rest.'
            : 'Nothing was changed. Retry, or close and try again later.'}
        </Box>
      )}
    </Box>
  );
}

SaveProgress.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['pending', 'running', 'done', 'failed']).isRequired,
      error: PropTypes.string,
    })
  ).isRequired,
};

export default SaveProgress;
